/**
 * portalWriteError — human-copy error text for a portal / report WRITE mutation.
 *
 * A write can fail two ways, and they deserve different copy:
 *
 *   • The backend answered a non-2xx with a REAL, human message — e.g. a 409
 *     conflict ("Our team has finalised this one…"). fetchAPI throws an Error
 *     carrying that message + a `.status`. We surface it verbatim: it's actionable.
 *
 *   • The request never got a usable answer — a network drop, or (before the
 *     backend's CORS-on-500 fix) a 500 whose Access-Control-Allow-Origin header was
 *     stripped, so the browser rejected it. `fetch()` rejects with a TypeError whose
 *     message is a browser-specific technical string ("Failed to fetch" /
 *     "Load failed" / "NetworkError…") and NO status. A raw 500 body is likewise
 *     generic ("Internal server error"). Surfacing either reads as a bug, so we show
 *     the caller's plain fallback instead.
 *
 * Rule: return the backend message only when it's a real client-level message
 * (has a status < 500 and isn't an opaque network string); otherwise the fallback.
 * This is what un-sticks the "silent failure": every call site passes a plain,
 * human fallback, so ANY non-2xx / network failure now yields legible toast copy.
 */

// Browser-specific strings `fetch()` uses when the request never completed. Lower-cased.
const OPAQUE_NETWORK_MESSAGES = [
  'failed to fetch', // Chromium
  'load failed', // Safari
  'networkerror', // Firefox ("NetworkError when attempting to fetch resource.")
  'network request failed',
];

function isOpaqueNetworkMessage(message: string): boolean {
  const m = message.toLowerCase();
  return OPAQUE_NETWORK_MESSAGES.some((needle) => m.includes(needle));
}

export function portalWriteError(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message && !isOpaqueNetworkMessage(err.message)) {
    const status = (err as { status?: number }).status;
    // A real client-level message (validation, conflict, permission). 500-class
    // bodies are generic, so we prefer the caller's plain fallback for those.
    if (status === undefined || status < 500) return err.message;
  }
  return fallback;
}
